import React, { useState, useEffect, useRef } from 'react';
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Play, Pause, RotateCcw, Settings, Volume2, VolumeX } from "lucide-react";
import { ContextualNotificationPrompt } from "@/components/notifications/ContextualNotificationPrompt";
import WhyThisMattersCard from "@/components/education/WhyThisMattersCard";

type BreathPattern = 'box' | 'coherence' | 'extended';

interface BreathworkAnimationProps {
  className?: string;
}

const BreathworkAnimation: React.FC<BreathworkAnimationProps> = ({ className }) => {
  const [isActive, setIsActive] = useState(false);
  const [currentPattern, setCurrentPattern] = useState<BreathPattern>('coherence');
  const [currentPhase, setCurrentPhase] = useState<'inhale' | 'hold' | 'exhale' | 'hold2'>('inhale');
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [totalTime, setTotalTime] = useState(300); // 5 minutes default
  const [showSettings, setShowSettings] = useState(false);
  const [phaseCountdown, setPhaseCountdown] = useState(0);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [circleProgress, setCircleProgress] = useState(0);
  const [particles, setParticles] = useState<Array<{id: number, x: number, y: number, delay: number}>>([]);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const phaseTimeRef = useRef<NodeJS.Timeout | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);

  const breathPatterns = {
    box: {
      name: 'Box Breathing',
      description: 'Equal counts for inhale, hold, exhale, hold (4-4-4-4)',
      inhale: 4,
      hold: 4,
      exhale: 4,
      hold2: 4,
      color: 'hsl(var(--primary))',
      gradient: 'from-primary/20 via-primary/10 to-transparent'
    },
    coherence: {
      name: 'Coherence Breathing',
      description: 'Balanced breathing for heart-brain coherence (5-5)',
      inhale: 5,
      hold: 0,
      exhale: 5,
      hold2: 0,
      color: 'hsl(var(--secondary))',
      gradient: 'from-secondary/20 via-secondary/10 to-transparent'
    },
    extended: {
      name: 'Extended Exhale',
      description: 'Longer exhale to activate relaxation response (4-8)',
      inhale: 4,
      hold: 0,
      exhale: 8,
      hold2: 0,
      color: 'hsl(var(--accent))',
      gradient: 'from-accent/20 via-accent/10 to-transparent'
    }
  };
  
  // Generate floating particles for enhanced visual feedback
  useEffect(() => {
    if (isActive) {
      const newParticles = Array.from({ length: 8 }, (_, i) => ({
        id: i,
        x: Math.random() * 360,
        y: Math.random() * 360,
        delay: Math.random() * 2
      }));
      setParticles(newParticles);
    } else {
      setParticles([]);
    }
  }, [isActive, currentPhase]);

  const currentPatternConfig = breathPatterns[currentPattern];

  useEffect(() => {
    if (isActive) {
      // Start the session timer
      setTimeRemaining(totalTime);
      intervalRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            setIsActive(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Start the breathing cycle
      startBreathingCycle();
    } else {
      // Clean up timers
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (phaseTimeRef.current) {
        clearTimeout(phaseTimeRef.current);
        phaseTimeRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (phaseTimeRef.current) clearTimeout(phaseTimeRef.current);
    };
  }, [isActive, currentPattern, totalTime]);

  // Audio functions
  const playBreathSound = (frequency: number, duration: number) => {
    if (!audioEnabled) return;
    
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      
      const oscillator = audioContextRef.current.createOscillator();
      const gainNode = audioContextRef.current.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContextRef.current.destination);
      
      oscillator.frequency.setValueAtTime(frequency, audioContextRef.current.currentTime);
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0, audioContextRef.current.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.1, audioContextRef.current.currentTime + 0.1);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContextRef.current.currentTime + duration);
      
      oscillator.start(audioContextRef.current.currentTime);
      oscillator.stop(audioContextRef.current.currentTime + duration);
    } catch (error) {
      // Audio not supported
    }
  };

  const playBeep = () => {
    playBreathSound(800, 0.2);
  };

  const startBreathingCycle = () => {
    const runCycle = () => {
      if (!isActive) return;

      const pattern = breathPatterns[currentPattern];
      
      // Inhale phase
      setCurrentPhase('inhale');
      setCircleProgress(0);
      startPhaseCountdown(pattern.inhale, 'inhale');
      if (audioEnabled) playBeep(); // Initial beep
      
      phaseTimeRef.current = setTimeout(() => {
        if (!isActive) return;
        
        // Hold phase 1 (after inhale)
        if (pattern.hold > 0) {
          setCurrentPhase('hold');
          setCircleProgress(100);
          startPhaseCountdown(pattern.hold, 'hold');
          
          phaseTimeRef.current = setTimeout(() => {
            if (!isActive) return;
            
            // Exhale phase
            setCurrentPhase('exhale');
            setCircleProgress(100);
            startPhaseCountdown(pattern.exhale, 'exhale');
            if (audioEnabled) playBeep(); // Initial beep for exhale
            
            phaseTimeRef.current = setTimeout(() => {
              if (!isActive) return;
              
              // Hold phase 2 (after exhale)
              if (pattern.hold2 > 0) {
                setCurrentPhase('hold2');
                setCircleProgress(0);
                startPhaseCountdown(pattern.hold2, 'hold');
                
                phaseTimeRef.current = setTimeout(() => {
                  runCycle(); // Start next cycle
                }, pattern.hold2 * 1000);
              } else {
                runCycle(); // Start next cycle
              }
            }, pattern.exhale * 1000);
          }, pattern.hold * 1000);
        } else {
          // No hold, go straight to exhale
          setCurrentPhase('exhale');
          setCircleProgress(100);
          startPhaseCountdown(pattern.exhale, 'exhale');
          if (audioEnabled) playBeep(); // Initial beep for exhale
          
          phaseTimeRef.current = setTimeout(() => {
            runCycle(); // Start next cycle
          }, pattern.exhale * 1000);
        }
      }, pattern.inhale * 1000);
    };

    runCycle();
  };

  const startPhaseCountdown = (duration: number, phaseType: string) => {
    setPhaseCountdown(duration);
    let count = duration - 1;
    
    // Update circle progress for coherence breathing
    if (currentPattern === 'coherence') {
      let progressCount = 0;
      const progressInterval = setInterval(() => {
        if (!isActive) {
          clearInterval(progressInterval);
          return;
        }
        
        progressCount++;
        if (phaseType === 'inhale') {
          setCircleProgress((progressCount / duration) * 100);
        } else if (phaseType === 'exhale') {
          setCircleProgress(100 - (progressCount / duration) * 100);
        }
        
        if (progressCount >= duration) {
          clearInterval(progressInterval);
        }
      }, 1000);
    }
    
    const countdownInterval = setInterval(() => {
      if (!isActive) {
        clearInterval(countdownInterval);
        return;
      }
      
      if (count > 0) {
        setPhaseCountdown(count);
        // Play gentle beep only at transitions for less intrusive experience
        if (audioEnabled && count === Math.floor(duration / 2) && (phaseType === 'inhale' || phaseType === 'exhale')) {
          playBreathSound(phaseType === 'inhale' ? 440 : 330, 0.3);
        }
        count--;
      } else {
        setPhaseCountdown(0);
        clearInterval(countdownInterval);
      }
    }, 1000);
  };

  const toggleBreathing = () => {
    setIsActive(!isActive);
  };

  const resetSession = () => {
    setIsActive(false);
    setTimeRemaining(totalTime);
    setCurrentPhase('inhale');
    setPhaseCountdown(0);
    setCircleProgress(0);
    if (countdownRef.current) {
      clearTimeout(countdownRef.current);
    }
  };

  const getAnimationSize = () => {
    switch (currentPhase) {
      case 'inhale':
        return 'scale-150';
      case 'hold':
        return 'scale-150';
      case 'exhale':
        return 'scale-100';
      case 'hold2':
        return 'scale-100';
      default:
        return 'scale-110';
    }
  };

  const getPhaseText = () => {
    switch (currentPhase) {
      case 'inhale':
        return 'Breathe In';
      case 'hold':
        return 'Hold';
      case 'exhale':
        return 'Breathe Out';
      case 'hold2':
        return 'Hold';
      default:
        return 'Ready';
    }
  };

  const getPhaseCount = () => {
    const pattern = breathPatterns[currentPattern];
    switch (currentPhase) {
      case 'inhale':
        return pattern.inhale;
      case 'hold':
        return pattern.hold;
      case 'exhale':
        return pattern.exhale;
      case 'hold2':
        return pattern.hold2;
      default:
        return 0;
    }
  };

  return (
    <div className={className}>
      {/* Pattern Selection */}
      <Card className="p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold">Choose Your Breathing Pattern</h4>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowSettings(!showSettings)}
          >
            <Settings className="h-4 w-4" />
          </Button>
        </div>
        
        <Select value={currentPattern} onValueChange={(value) => setCurrentPattern(value as BreathPattern)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(breathPatterns).map(([key, pattern]) => (
              <SelectItem key={key} value={key}>
                <div>
                  <div className="font-medium">{pattern.name}</div>
                  <div className="text-xs text-muted-foreground">{pattern.description}</div>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {showSettings && (
          <div className="mt-4 p-3 bg-muted/30 rounded-lg">
            <label className="text-sm font-medium block mb-2">Session Duration</label>
            <Select value={totalTime.toString()} onValueChange={(value) => setTotalTime(parseInt(value))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="60">1 minute</SelectItem>
                <SelectItem value="180">3 minutes</SelectItem>
                <SelectItem value="300">5 minutes</SelectItem>
                <SelectItem value="600">10 minutes</SelectItem>
                <SelectItem value="900">15 minutes</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </Card>

      {/* Breathing Animation */}
      <Card className="p-8 text-center bg-gradient-to-br from-background to-muted/30">
        <div className="flex flex-col items-center space-y-6">
          {/* Timer */}
          <div className="text-sm text-muted-foreground">
            {Math.floor(timeRemaining / 60)}:{(timeRemaining % 60).toString().padStart(2, '0')} remaining
          </div>

          {/* Breathing Animation */}
          <div className="relative w-64 h-64 flex items-center justify-center">
            {/* Box Breathing Animation */}
            {currentPattern === 'box' && (
              <div className="absolute inset-8">
                {/* Phase text above box */}
                <div className="absolute -top-16 left-1/2 transform -translate-x-1/2">
                  <div 
                    className="text-2xl font-bold text-center"
                    style={{ 
                      color: currentPatternConfig.color,
                      textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
                    }}
                  >
                    {getPhaseText()}
                  </div>
                  {isActive && (
                    <div 
                      className="text-lg text-center mt-1"
                      style={{ 
                        color: currentPatternConfig.color,
                        opacity: 0.8
                      }}
                    >
                      {getPhaseCount()}s
                    </div>
                  )}
                </div>

                <svg width="192" height="192" className="absolute inset-0">
                  {/* Box outline */}
                  <rect 
                    x="16" y="16" 
                    width="160" height="160" 
                    fill="none" 
                    stroke={currentPatternConfig.color} 
                    strokeWidth="2" 
                    strokeOpacity="0.2"
                  />
                  
                  {/* Animated drawing line */}
                  {isActive && (
                    <>
                      {/* Top line - Inhale */}
                      {currentPhase === 'inhale' && (
                        <line 
                          x1="16" y1="16" 
                          x2={16 + (160 * (getPhaseCount() - phaseCountdown) / getPhaseCount())} 
                          y2="16"
                          stroke={currentPatternConfig.color}
                          strokeWidth="8"
                          className="animate-pulse"
                        />
                      )}
                      
                      {/* Right line - Hold 1 */}
                      {currentPhase === 'hold' && (
                        <>
                          <line x1="16" y1="16" x2="176" y2="16" stroke={currentPatternConfig.color} strokeWidth="8" />
                          <line 
                            x1="176" y1="16" 
                            x2="176" 
                            y2={16 + (160 * (getPhaseCount() - phaseCountdown) / getPhaseCount())}
                            stroke={currentPatternConfig.color}
                            strokeWidth="8"
                            className="animate-pulse"
                          />
                        </>
                      )}
                      
                      {/* Bottom line - Exhale */}
                      {currentPhase === 'exhale' && (
                        <>
                          <line x1="16" y1="16" x2="176" y2="16" stroke={currentPatternConfig.color} strokeWidth="8" />
                          <line x1="176" y1="16" x2="176" y2="176" stroke={currentPatternConfig.color} strokeWidth="8" />
                          <line 
                            x1="176" y1="176" 
                            x2={176 - (160 * (getPhaseCount() - phaseCountdown) / getPhaseCount())} 
                            y2="176"
                            stroke={currentPatternConfig.color}
                            strokeWidth="8"
                            className="animate-pulse"
                          />
                        </>
                      )}
                      
                      {/* Left line - Hold 2 */}
                      {currentPhase === 'hold2' && (
                        <>
                          <line x1="16" y1="16" x2="176" y2="16" stroke={currentPatternConfig.color} strokeWidth="8" />
                          <line x1="176" y1="16" x2="176" y2="176" stroke={currentPatternConfig.color} strokeWidth="8" />
                          <line x1="176" y1="176" x2="16" y2="176" stroke={currentPatternConfig.color} strokeWidth="8" />
                          <line 
                            x1="16" y1="176" 
                            x2="16" 
                            y2={176 - (160 * (getPhaseCount() - phaseCountdown) / getPhaseCount())}
                            stroke={currentPatternConfig.color}
                            strokeWidth="8"
                            className="animate-pulse"
                          />
                        </>
                      )}
                    </>
                  )}
                </svg>
              </div>
            )}
            
            {/* Main breathing circle with enhanced effects */}
            {currentPattern !== 'box' && (
              <div className="relative">
                {/* Outer glow rings */}
                <div 
                  className={`absolute inset-0 w-48 h-48 rounded-full bg-gradient-to-r ${currentPatternConfig.gradient} transition-all ease-in-out ${getAnimationSize()}`}
                  style={{ 
                    transitionDuration: isActive ? `${getPhaseCount() * 1000}ms` : '500ms',
                    filter: isActive ? 'blur(20px)' : 'blur(10px)',
                    opacity: isActive ? 0.6 : 0.2
                  }}
                />
                
                {/* Middle glow */}
                <div 
                  className={`absolute inset-0 w-44 h-44 m-auto rounded-full transition-all ease-in-out ${getAnimationSize()}`}
                  style={{ 
                    backgroundColor: currentPatternConfig.color,
                    transitionDuration: isActive ? `${getPhaseCount() * 1000}ms` : '500ms',
                    filter: 'blur(10px)',
                    opacity: isActive ? 0.4 : 0.1
                  }}
                />
                
                {/* Main breathing circle */}
                <div 
                  className={`w-40 h-40 rounded-full transition-all ease-in-out ${getAnimationSize()} relative overflow-hidden`}
                  style={{ 
                    backgroundColor: currentPatternConfig.color,
                    opacity: isActive ? 0.85 : 0.3,
                    transitionDuration: isActive ? `${getPhaseCount() * 1000}ms` : '500ms',
                    boxShadow: isActive ? `0 0 40px ${currentPatternConfig.color}, inset 0 0 20px rgba(255,255,255,0.2)` : 'none'
                  }}
                >
                  {/* Inner shimmer effect */}
                  {isActive && (
                    <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent animate-shimmer" />
                  )}
                </div>
                
                {/* Floating particles */}
                {isActive && particles.map((particle) => (
                  <div
                    key={particle.id}
                    className="absolute w-1 h-1 rounded-full bg-white/60"
                    style={{
                      left: '50%',
                      top: '50%',
                      animation: `float-particle 3s ease-in-out infinite`,
                      animationDelay: `${particle.delay}s`,
                      transform: `rotate(${particle.x}deg) translateY(-${80 + particle.y % 40}px)`
                    }}
                  />
                ))}
                
                {/* Progress circle for coherence breathing */}
                {currentPattern === 'coherence' && isActive && (
                  <svg className="absolute inset-0 w-64 h-64 -rotate-90">
                    <circle
                      cx="128"
                      cy="128"
                      r="100"
                      fill="none"
                      stroke={currentPatternConfig.color}
                      strokeWidth="6"
                      strokeOpacity="0.8"
                      strokeDasharray={`${2 * Math.PI * 100}`}
                      strokeDashoffset={`${2 * Math.PI * 100 * (1 - circleProgress / 100)}`}
                      style={{
                        transitionDuration: isActive ? `${getPhaseCount() * 1000}ms` : '500ms'
                      }}
                      className="transition-all ease-linear"
                    />
                  </svg>
                )}
              </div>
            )}
            
            {/* Large countdown number */}
            {isActive && phaseCountdown > 0 && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div 
                  className="text-6xl font-bold text-primary animate-pulse"
                  style={{ 
                    color: currentPattern === 'box' ? currentPatternConfig.color : 'hsl(var(--primary-foreground))',
                    textShadow: currentPattern === 'box' ? '2px 2px 4px rgba(0,0,0,0.3)' : 'none'
                  }}
                >
                  {phaseCountdown}
                </div>
              </div>
            )}
            
            {/* Phase text overlay */}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              {(!isActive || phaseCountdown === 0) && currentPattern !== 'box' && (
                <>
                  <div 
                    className="text-xl font-semibold mb-2"
                    style={{ 
                      color: 'hsl(var(--foreground))',
                    }}
                  >
                    {getPhaseText()}
                  </div>
                  {!isActive && (
                    <div 
                      className="text-sm opacity-70"
                      style={{ 
                        color: 'hsl(var(--muted-foreground))'
                      }}
                    >
                      {currentPatternConfig.description.split('(')[1]?.replace(')', '') || ''}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Instructions */}
          <div className="text-center">
            <h4 className="font-semibold mb-2">{currentPatternConfig.name}</h4>
            <p className="text-sm text-muted-foreground mb-4">
              {currentPatternConfig.description}
            </p>
            
            {!isActive && (
              <div className="text-xs text-muted-foreground">
                Find a comfortable position and breathe naturally. 
                When ready, press play to begin guided breathing.
              </div>
            )}
          </div>

          {/* Controls */}
          <div className="flex items-center gap-3">
            <Button 
              onClick={toggleBreathing}
              className="flex items-center gap-2"
              size="lg"
            >
              {isActive ? (
                <>
                  <Pause className="h-4 w-4" />
                  Pause
                </>
              ) : (
                <>
                  <Play className="h-4 w-4" />
                  Start
                </>
              )}
            </Button>
            
            <Button 
              variant="outline" 
              onClick={resetSession}
              className="flex items-center gap-2"
            >
              <RotateCcw className="h-4 w-4" />
              Reset
            </Button>
            
            <Button 
              variant="outline"
              size="lg"
              onClick={() => setAudioEnabled(!audioEnabled)}
              className="flex items-center gap-2"
            >
              {audioEnabled ? (
                <>
                  <Volume2 className="h-4 w-4" />
                  Sound On
                </>
              ) : (
                <>
                  <VolumeX className="h-4 w-4" />
                  Sound Off
                </>
              )}
            </Button>
          </div>
        </div>
      </Card>

      {/* Benefits */}
      <Card className="p-4 mt-4 bg-muted/20">
        <h5 className="font-medium mb-2">Benefits of {currentPatternConfig.name}</h5>
        <div className="text-sm text-muted-foreground space-y-1">
          {currentPattern === 'box' && (
            <>
              <p>• Reduces anxiety and stress</p>
              <p>• Improves focus and concentration</p>
              <p>• Balances the nervous system</p>
            </>
          )}
          {currentPattern === 'coherence' && (
            <>
              <p>• Synchronizes heart and brain rhythms</p>
              <p>• Enhances emotional balance</p>
              <p>• Increases mental clarity</p>
            </>
          )}
          {currentPattern === 'extended' && (
            <>
              <p>• Activates the relaxation response</p>
              <p>• Calms the nervous system</p>
              <p>• Prepares the body for rest</p>
            </>
          )}
        </div>
      </Card>

      {/* Education Card */}
      <WhyThisMattersCard
        toolName="Breathwork"
        shortDescription="Conscious breathing directly activates your parasympathetic nervous system."
        whyItMatters="Breathwork is one of the few tools that gives you direct, voluntary control over your autonomic nervous system. By changing your breathing pattern, you can shift from fight-or-flight to rest-and-digest within minutes."
        researchSummary="Studies show that slow, controlled breathing reduces cortisol levels by up to 25%, lowers blood pressure, and increases heart rate variability - a key marker of stress resilience."
        howToOptimise="Practise for 5-10 minutes daily, ideally at the same time. Extended exhales (longer out than in) maximise the calming effect. Pair with grounding or visualisation for deeper impact."
        tags={['Stress Relief', 'Nervous System', 'HRV']}
        researchLinks={[
          { title: 'Breathing Practices and Stress Response - NIH', url: 'https://pubmed.ncbi.nlm.nih.gov/30571622/' }
        ]}
      />

      {/* Contextual notification prompt */}
      <ContextualNotificationPrompt context="breathwork" />
    </div>
  );
};

export default BreathworkAnimation;